import { useDistributionChannels } from '@src/stores-v2/StoreContext';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';

import type { MutableRefObject } from 'react';

import ChannelIcon from './ChannelIcon';

import type { DistributionChannel } from '@src/types.ts/distribution-channel';

interface Props {
  showcaseId: string;
  enabledIds: Set<string>;
  onPick: (channel: DistributionChannel) => void;
  onClose: () => void;
}

const AddChannelPicker = observer(
  ({ showcaseId, enabledIds, onPick, onClose }: Props) => {
    const distributionChannelsStore = useDistributionChannels();
    const ref = useRef<HTMLDivElement>(null);
    // Hold the latest onClose in a ref so the dismiss-listener effect can run
    // once on mount instead of re-attaching every parent render (parents
    // typically pass an inline arrow).
    const onCloseRef: MutableRefObject<() => void> = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
      void distributionChannelsStore.loadAvailableForShowcase(showcaseId);
    }, [showcaseId, distributionChannelsStore]);

    useEffect(() => {
      const onDown = (e: MouseEvent) => {
        if (ref.current && !ref.current.contains(e.target as Node)) {
          onCloseRef.current();
        }
      };
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onCloseRef.current();
      };
      document.addEventListener('mousedown', onDown);
      document.addEventListener('keydown', onKey);
      return () => {
        document.removeEventListener('mousedown', onDown);
        document.removeEventListener('keydown', onKey);
      };
    }, []);

    const state = distributionChannelsStore.getAvailableState(showcaseId);
    const candidates = useMemo(
      () => state.channels.filter(c => !enabledIds.has(c.id)),
      [state.channels, enabledIds],
    );

    return (
      <Panel ref={ref} role="dialog" aria-label="Add distribution channel">
        <Header>Add a channel</Header>
        <Body>
          {state.status === 'loading' && state.channels.length === 0 && (
            <Muted>Loading…</Muted>
          )}
          {state.status === 'error' && state.channels.length === 0 && (
            <ErrorRow>
              <span>{state.error ?? 'Failed to load.'}</span>
              <RetryButton
                type="button"
                onClick={() =>
                  void distributionChannelsStore.loadAvailableForShowcase(
                    showcaseId,
                    { force: true },
                  )
                }
              >
                Retry
              </RetryButton>
            </ErrorRow>
          )}
          {state.status !== 'loading' &&
            state.status !== 'error' &&
            candidates.length === 0 && (
              <Muted>All available channels are already enabled.</Muted>
            )}
          {candidates.length > 0 && (
            <List>
              {candidates.map(channel => (
                <Item
                  key={channel.id}
                  type="button"
                  onClick={() => onPick(channel)}
                >
                  <ChannelIcon
                    iconUrl={channel.iconUrl}
                    alt={`${channel.title} icon`}
                    size={24}
                  />
                  <ItemText>
                    <strong>{channel.title}</strong>
                    {channel.description && <small>{channel.description}</small>}
                  </ItemText>
                </Item>
              ))}
            </List>
          )}
        </Body>
      </Panel>
    );
  },
);

export default AddChannelPicker;

const Panel = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 100;
  width: 320px;
  max-height: 360px;
  display: flex;
  flex-direction: column;
  background: var(--popover);
  color: var(--popover-foreground);
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  box-shadow: var(--elevation-lg, 0 10px 30px rgba(0, 0, 0, 0.18));
  overflow: hidden;
`;

const Header = styled.div`
  padding: 10px 12px;
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  border-bottom: 1px solid var(--border-subtle);
`;

const Body = styled.div`
  padding: 6px;
  overflow-y: auto;
`;

const Muted = styled.div`
  padding: 16px 8px;
  font-size: var(--text-sm);
  color: var(--text-secondary);
  text-align: center;
`;

const ErrorRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 8px;
  font-size: var(--text-sm);
  color: var(--text-secondary);
`;

const RetryButton = styled.button`
  align-self: flex-start;
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 4px 10px;
  color: var(--foreground);
  font-size: var(--text-sm);
  cursor: pointer;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const Item = styled.button`
  display: flex;
  gap: 10px;
  align-items: center;
  background: transparent;
  border: none;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: var(--surface-elevated);
  }
`;

const ItemText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  > strong {
    font-size: var(--text-sm);
    font-weight: var(--font-weight-medium);
    color: var(--foreground);
  }

  > small {
    font-size: 12px;
    color: var(--text-secondary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;
